import { selectCompanies } from '@/features/domain/company'
import { selectTerritories } from '@/features/domain/territory'
import type {
  Creation,
  DriversRestrictionValue,
  UserFormValues,
  VehiclesRestrictionValue,
} from '../types'

const defaultValues: UserFormValues = {
  id: '',
  name: '',
  type: 'planner',
  email: '',
  limitedAccess: false,
  surname: '',
  enabled: true,
  username: '',
  territoryFilter: [],
  courierFilter: [],
  isCompanyFiltered: false,
  companies: [],
  'new-password': '',
  vehicles: {},
  drivers: {},
}

export function computeFormInitialValues(
  creation: Creation,
  territories: ReturnType<typeof selectTerritories>,
  companies: ReturnType<typeof selectCompanies>,
  allCompaniesCount: number,
): UserFormValues {
  return creation.creating
    ? defaultValues
    : computeEditOverrideValues(creation.user, territories, companies, allCompaniesCount)
}

function computeEditOverrideValues(
  user: uui.domain.client.rm.RouteManagerUser,
  territories: ReturnType<typeof selectTerritories>,
  companies: ReturnType<typeof selectCompanies>,
  allCompaniesCount: number,
): UserFormValues {
  const territoryFilter = [...user.territoryFilter]
  const { name, surname, username, email, id, type, enabled } = user

  const courierFilterValue = user.type === 'courier' ? Object.keys(user.courierFilter) : []

  const drivers: Record<string, { restriction: DriversRestrictionValue; ids: string[] }> =
    user.type === 'courier'
      ? Object.fromEntries(
          Object.entries(user.courierFilter).map(([key, value]) => [
            key,
            value.driverIds.includes('*')
              ? { restriction: 'not-restricted', ids: [] }
              : { restriction: 'restricted', ids: value.driverIds.map(id => id) },
          ]),
        )
      : {}

  const vehicles: Record<string, { restriction: VehiclesRestrictionValue; ids: string[] }> =
    user.type === 'courier'
      ? Object.fromEntries(
          Object.entries(user.courierFilter).map(([key, value]) => [
            key,
            value.vehicleIds.includes('*')
              ? { restriction: 'not-restricted', ids: [] }
              : { restriction: 'restricted', ids: value.vehicleIds.map(id => id) },
          ]),
        )
      : {}

  const companiesValue =
    user.companies.length > 0 ? user.companies.map(companyId => companies[companyId]) : []

  const values = {
    id,
    name,
    type,
    email,
    limitedAccess: false,
    surname,
    enabled,
    username,
    territoryFilter: [] as uui.domain.client.rm.Territory[],
    courierFilter: courierFilterValue,
    isCompanyFiltered:
      user.type === 'guest' && allCompaniesCount > 0 ? user.isCompanyFiltered : false,
    companies: companiesValue,
    'new-password': '',
    vehicles,
    drivers,
  }

  if (territoryFilter[0] !== '*') {
    values.limitedAccess = true
    values.territoryFilter = Object.values(territories).filter(territory =>
      territoryFilter.includes(territory.id),
    )
  }

  return values
}
